import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import {
    CheckCircleOutline,
    ChevronRight,
    ErrorOutline,
    IndeterminateCheckBox,
    WorkOutline,
} from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import { useNotifications } from '@/providers/NotificationsProvider/NotificationsProvider';
import type { Notification } from '@/types/types';
import { timePassed } from '@/helpers/format';
import type { NotificationFilterId } from './INotificationsPopover';
import { styles } from './styles';

interface IconConfig {
    icon: typeof WorkOutline;
    color: string;
}

const tabs: { id: NotificationFilterId; label: string; readValue: boolean | null }[] = [
    { id: 'all', label: 'All', readValue: null },
    { id: 'unread', label: 'Unread', readValue: false },
    { id: 'read', label: 'Read', readValue: true },
];

const useIconConfig = (title: string): IconConfig => {
    const theme = useTheme();
    const map: Record<string, IconConfig> = {
        'New job': { icon: WorkOutline, color: theme.palette.blue[600] },
        'New Job': { icon: WorkOutline, color: theme.palette.blue[600] },
        'New Supplement': {
            icon: IndeterminateCheckBox,
            color: theme.palette.yellow[500],
        },
        'Service Supplemented': {
            icon: IndeterminateCheckBox,
            color: theme.palette.yellow[500],
        },
        'Supplement Approved': {
            icon: CheckCircleOutline,
            color: theme.palette.green[500],
        },
        'Supplement Accepted': {
            icon: CheckCircleOutline,
            color: theme.palette.green[500],
        },
        'Supplement Declined': {
            icon: ErrorOutline,
            color: theme.palette.red[700],
        },
    };
    return map[title] ?? { icon: WorkOutline, color: theme.palette.red[700] };
};

const NotificationRow = ({ n, onClick }: { n: Notification; onClick: (n: Notification) => void }) => {
    const theme = useTheme();
    const iconCfg = useIconConfig(n.title);
    const Icon = iconCfg.icon;
    return (
        <Box sx={styles.item(theme)} onClick={() => onClick(n)}>
            {!n.read ? (
                <Box component="span" sx={styles.unreadDot(theme)} />
            ) : (
                <Box component="span" sx={styles.dotPlaceholder} />
            )}
            <Box sx={styles.iconCircle(iconCfg.color)}>
                <Icon />
            </Box>
            <Box sx={styles.itemText}>
                <Typography sx={styles.itemTitle}>{n.title}</Typography>
                <Typography sx={styles.itemTime}>
                    {timePassed(n.timestamp)}
                    {n.read ? ' · Read' : ''}
                </Typography>
            </Box>
            <ChevronRight sx={styles.chevron} />
        </Box>
    );
};

const NotificationsPopover = () => {
    const theme = useTheme();
    const { state, actions } = useNotifications();
    const [filter, setFilter] = useState<NotificationFilterId>('all');

    const filtered = useMemo(() => {
        const readValue = tabs.find((t) => t.id === filter)?.readValue ?? null;
        const list =
            readValue === null
                ? state.notifications
                : state.notifications.filter((n) => n.read === readValue);
        return [...list].sort((a, b) => {
            if (!a.timestamp) return 1;
            if (!b.timestamp) return -1;
            return b.timestamp - a.timestamp;
        });
    }, [filter, state.notifications]);

    return (
        <Box sx={styles.root}>
            <Typography sx={styles.title}>Notifications</Typography>
            <Box sx={styles.tabsRow}>
                {tabs.map((t) => (
                    <Button
                        key={t.id}
                        sx={styles.tabBtn(filter === t.id, theme)}
                        onClick={() => setFilter(t.id)}
                    >
                        {t.label}
                    </Button>
                ))}
            </Box>
            <Box sx={styles.list}>
                {filtered.length === 0 ? (
                    <Box sx={styles.listEmpty}>No notifications</Box>
                ) : (
                    filtered.map((n) => (
                        <NotificationRow
                            key={n.id}
                            n={n}
                            onClick={(notif) => void actions.markAsRead(notif)}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
};

export default NotificationsPopover;
